'use client';

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type FilterFn,
  type Row,
  type SortingState,
} from '@tanstack/react-table';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { Colors, TextVariants } from '@lichtblick/theme';
import { TableSorterColumns, ListSortDirection } from '@lichtblick/types';

import {
  Flex,
  Table,
  TableHeader,
  TableContainer,
  TableThead,
  TableData,
  LazyLoadTrigger,
  CheckboxContainer,
} from './Table.styles';

import { Checkbox } from '../Checkbox';
import { Collapse } from '../Collapse';
import { TableSorter } from '../TableSorter';
import { Text } from '../Text';

type Props<T, F> = {
  collapseContent?: (row: Row<T>) => React.ReactElement;
  columns: ColumnDef<T>[];
  data: F[];
  enableRowSelection?: boolean;
  filteredHeaders?: TableSorterColumns;
  hrefBuilder?: (row: Row<T>) => string;
  isFullHeight?: boolean;
  onCustomSort?: (property: string, direction?: ListSortDirection) => void;
  onPreFetch?: (id: string) => void;
  onRowSelectionChange?: (selectedRows: Set<string | number>) => void;
  openRows?: Record<string, boolean>;
  rowIdAccessor?: keyof F | ((row: F) => string | number);
  selectedRows?: Set<string | number>;
  setInView?: (input: boolean) => void;
};

const fuzzyFilter: FilterFn<unknown> = (row, columnId, value) => {
  const rowValue = row.getValue(columnId);

  return String(rowValue).toLowerCase().includes(String(value).toLowerCase());
};

export const GenericTable = <T, F>({
  collapseContent,
  columns,
  data,
  enableRowSelection = false,
  filteredHeaders,
  hrefBuilder,
  isFullHeight = true,
  onCustomSort,
  onPreFetch,
  onRowSelectionChange,
  openRows,
  rowIdAccessor = 'id' as keyof F,
  selectedRows = new Set(),
  setInView,
}: Props<T, F>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [counter, setCounter] = useState(0);

  const { inView: isInView, ref } = useInView();

  const getRowId = (row: F): string | number => {
    if (typeof rowIdAccessor === 'function') {
      return rowIdAccessor(row);
    }

    return row[rowIdAccessor] as string | number;
  };

  const handleRowSelection = (rowId: string | number) => {
    const newSelection = new Set(selectedRows);

    if (newSelection.has(rowId)) {
      newSelection.delete(rowId);
    } else {
      newSelection.add(rowId);
    }

    onRowSelectionChange?.(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === data.length) {
      onRowSelectionChange?.(new Set());
    } else {
      const allIds = data.map(getRowId);

      onRowSelectionChange?.(new Set(allIds));
    }
  };

  const checkboxColumn: ColumnDef<T> = {
    id: 'select',
    header: () => (
      <Checkbox isChecked={selectedRows.size === data.length && data.length > 0} onChange={handleSelectAll} />
    ),
    cell: ({ row }) => (
      <CheckboxContainer>
        <Checkbox
          isChecked={selectedRows.has(getRowId(row.original as unknown as F))}
          onChange={() => handleRowSelection(getRowId(row.original as unknown as F))}
        />
      </CheckboxContainer>
    ),
    size: 40,
    enableSorting: false,
    meta: { shouldEscapeLink: true },
  };

  const tableColumns = enableRowSelection ? [checkboxColumn, ...columns] : columns;

  const table = useReactTable<T>({
    data: data as unknown as T[],
    columns: tableColumns,
    state: {
      sorting,
      globalFilter,
    },
    defaultColumn: {
      size: 150,
      minSize: 20,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter as FilterFn<T>,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    setInView?.(isInView);
  }, [isInView, setInView]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current?.scrollTo) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [counter]);

  const customSort = (property: string, direction: ListSortDirection | undefined) => {
    setCounter((prev) => prev + 1);
    onCustomSort?.(property, direction);
  };

  return (
    <TableContainer $isFullHeight={isFullHeight} ref={containerRef}>
      <Table>
        <TableThead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHeader
                    $isInteractive={header.column.getCanSort()}
                    colSpan={header.colSpan}
                    key={header.id}
                    style={{
                      width: header.getSize(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <Flex>
                        {header.column.columnDef.header != 'fold' && (
                          <Text
                            $variant={TextVariants.title.m}
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </Text>
                        )}
                        {header.column.getCanSort() && onCustomSort && (
                          <TableSorter
                            filteredHeaders={filteredHeaders}
                            headerId={header.id}
                            onCustomSort={customSort}
                          />
                        )}
                      </Flex>
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </TableThead>

        <tbody>
          {table.getRowModel().rows.map((row) => {
            return (
              <React.Fragment key={row.id}>
                <tr onMouseEnter={() => onPreFetch?.(String(getRowId(row.original as unknown as F)))}>
                  {row.getVisibleCells().map((cell) => (
                    <TableData
                      $isOpen={openRows ? openRows[row.id] : false}
                      key={cell.id}
                      style={{
                        width: `${cell.column.getSize()}px`,
                      }}
                    >
                      {cell.column.columnDef.meta?.shouldEscapeLink ? (
                        flexRender(cell.column.columnDef.cell, { ...cell.getContext() })
                      ) : (
                        <Link href={hrefBuilder?.(row) ?? ''} key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, { ...cell.getContext() })}
                        </Link>
                      )}
                    </TableData>
                  ))}
                </tr>
                {openRows && (
                  <tr>
                    <td colSpan={tableColumns.length}>
                      <Collapse
                        color={Colors.blue[3]}
                        content={collapseContent ? collapseContent(row) : null}
                        isOpen={openRows[row.id] ?? false}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          {table
            .getFooterGroups()
            // Without this there will be empty footer rows if your columns do not have footers
            .filter((footerGroup) =>
              footerGroup.headers.some((header) => header.column.columnDef.footer && !header.isPlaceholder),
            )
            .map((footerGroup) => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map((header) => (
                  <td key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                  </td>
                ))}
              </tr>
            ))}
        </tfoot>
      </Table>
      <div>
        <LazyLoadTrigger ref={ref} style={{ visibility: 'collapse' }} />
      </div>
    </TableContainer>
  );
};
